import _, {isUndefined, without} from 'lodash';
import React, {Fragment, ReactNode, useEffect, useMemo, useReducer, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {isTeammateNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {ColorDot} from '../../../../libs/shared/ui/src/components/utility/colorDot';
import {flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {DropdownItemInboxDot} from '../../../components/common/dropdowns/dropdownItemInboxDot';
import {BaseFilterDropdownProps} from '../../../components/common/filters/common/types';
import {ModalFilterDropdown} from '../../../components/common/filters/modalFilter/modalFilterDropdown';
import {ModalFilterItemProps} from '../../../components/common/filters/modalFilter/modalFilterItem';
import {ResourceMultiSelectFilter} from '../../../components/common/filters/resourceMultiSelectFilter';
import {FilterRowGenerator} from '../../../components/common/filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceAsync} from '../../../components/common/filters/sourceFilter/filterDropdownSource';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {inboxHasColor} from '../../../helpers/modelHelpers';
import {AnalyticsInboxModel} from '../../../models/api/analyticsFilterResourcesModel';
import {
  AnalyticsFilterTypesEnum,
  AnalyticsInboxScopeFiltersEnum,
  RAnalyticsInboxScopeFiltersEnum,
} from '../../../models/api/analyticsSavedViewModel';
import {getCompany} from '../../../store/data/company/companySelectors';
import {getSelectedTeamNamespaces} from '../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {AnalyticsPanelFiltersState} from '../../../store/panels/analytics/filters/analyticsPanelFiltersState';
import {getAnalyticsSelectedNamespace} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {getAnalyticsViewsAllInboxes} from '../../../store/panels/analytics/views/analyticsPanelViewsSelectors';
import {makeInboxesFetcher} from '../../helpers/analyticsFiltersHelpers';
import {useAnalyticsFilter} from '../../helpers/hooks/useAnalyticsFilter';
import {AnalyticsFilterButtonValue} from './common/analyticsFilterButtonValue';
import {analyticsFiltersConfig} from './common/analyticsFiltersConfig';
import {AnalyticsFilterItem, renderFilterTitle, useFiltersTracking} from './common/helpers';

/*
 * Types.
 */

enum InboxesFilterActionTypesEnum {
  DESELECT_INBOX = 'deselect-inbox',
  SET_INBOXES = 'set-inboxes',
  SELECT_SPECIAL_VALUE = 'select-special-value',
  CLEAR = 'clear',
  UPDATE_SELECTED_VALUE = 'update-selected-value',
}

type State =
  | {
      selectedInboxes: ReadonlyArray<number>;
      specialValue: undefined;
    }
  | {
      selectedInboxes: undefined;
      specialValue: AnalyticsInboxScopeFiltersEnum;
    };

type Action =
  | {type: InboxesFilterActionTypesEnum.DESELECT_INBOX; value: AnalyticsInboxModel}
  | {type: InboxesFilterActionTypesEnum.SET_INBOXES; values: ReadonlyArray<number>}
  | {type: InboxesFilterActionTypesEnum.CLEAR}
  | {
      type: InboxesFilterActionTypesEnum.UPDATE_SELECTED_VALUE;
      value: AnalyticsPanelFiltersState[AnalyticsFilterTypesEnum.INBOXES];
    }
  | {
      type: InboxesFilterActionTypesEnum.SELECT_SPECIAL_VALUE;
      value: AnalyticsInboxScopeFiltersEnum;
    };

/*
 * Constants.
 */

const {name, iconName, activeIconName} = analyticsFiltersConfig[AnalyticsFilterTypesEnum.INBOXES];

const inboxesFilterDefaultState: State = {
  selectedInboxes: undefined,
  specialValue: AnalyticsInboxScopeFiltersEnum.ALL_SHARED_INBOXES,
};

const specialValuesMap: Record<
  AnalyticsInboxScopeFiltersEnum,
  {value: AnalyticsInboxScopeFiltersEnum; name: ReactNode; id: string}
> = {
  [AnalyticsInboxScopeFiltersEnum.ALL_INBOXES]: {
    value: AnalyticsInboxScopeFiltersEnum.ALL_INBOXES,
    name: <Trans id="Analytics-inboxes-filter-modal-all">All inboxes</Trans>,
    id: 'inboxes-filer-all-inboxes',
  },
  [AnalyticsInboxScopeFiltersEnum.ALL_SHARED_INBOXES]: {
    value: AnalyticsInboxScopeFiltersEnum.ALL_SHARED_INBOXES,
    name: <Trans id="Analytics-inboxes-filter-modal-all-team">All shared inboxes</Trans>,
    id: 'inboxes-filter-all-team-inboxes',
  },
  [AnalyticsInboxScopeFiltersEnum.ALL_PRIVATE_INBOXES]: {
    value: AnalyticsInboxScopeFiltersEnum.ALL_PRIVATE_INBOXES,
    name: <Trans id="Analytics-inboxes-filter-modal-all-private">All individual inboxes</Trans>,
    id: 'inboxes-filter-all-private-inboxes',
  },
};

// This array determines the rendering order of our special values.
const specialValues = [
  specialValuesMap[AnalyticsInboxScopeFiltersEnum.ALL_INBOXES],
  specialValuesMap[AnalyticsInboxScopeFiltersEnum.ALL_SHARED_INBOXES],
  specialValuesMap[AnalyticsInboxScopeFiltersEnum.ALL_PRIVATE_INBOXES],
];

/*
 * Styles.
 */

const StyledInboxItemWrapper = styled.div`
  ${flexCenter}
`;

const StyledIcon = styled(Icon)`
  margin-left: 4px;
`;

const StyledColorDot = styled(ColorDot)`
  margin-right: 7px;
  display: inline-block;
`;

/*
 * Hook.
 */

export const useAnalyticsInboxesFilter = (): AnalyticsFilterItem => {
  const [selectedFilterValue, onUpdateFilter] = useAnalyticsFilter(AnalyticsFilterTypesEnum.INBOXES);
  const {trackOnEdit, trackOnRemove} = useFiltersTracking(AnalyticsFilterTypesEnum.INBOXES);
  const selectedTeamNamespaces = useAppSelector(getSelectedTeamNamespaces);
  const namespace = useAppSelector(getAnalyticsSelectedNamespace);
  const company = useAppSelector(getCompany);

  const savedViewsMetadataInboxes = useAppSelector(getAnalyticsViewsAllInboxes);
  const [fetchedInboxes, setFetchedInboxes] = useState<ReadonlyArray<AnalyticsInboxModel>>([]);

  // Merge of inboxes from the saved views metadata and the filter fetcher results.
  const inboxesMap = useMemo(
    () => _(fetchedInboxes).keyBy('id').merge(savedViewsMetadataInboxes).value(),
    [fetchedInboxes, savedViewsMetadataInboxes],
  );

  const [state, dispatchAction] = useReducer(inboxesFilterReducer, inboxesFilterDefaultState);
  const {selectedInboxes, specialValue} = state;
  const previousSelection = parseSelectedValue(selectedFilterValue);

  useEffect(() => {
    // This will keep the panel state in sync with the local state on mount and on update.
    dispatchAction({type: InboxesFilterActionTypesEnum.UPDATE_SELECTED_VALUE, value: selectedFilterValue});
  }, [selectedFilterValue]);

  useEffect(() => {
    if (!selectedInboxes) {
      return;
    }

    const updatedSelection = selectedInboxes.filter((inboxId) => {
      const inbox = inboxesMap[inboxId];

      if (!inbox) {
        return true;
      }

      if (selectedTeamNamespaces.size === 0 || selectedTeamNamespaces.has(inbox.namespace)) {
        return true;
      }

      // De-select this inbox as it is no longer available among the current namespaces.
      dispatchAction({type: InboxesFilterActionTypesEnum.DESELECT_INBOX, value: inbox});
      return false;
    });

    if (
      selectedFilterValue?.length !== updatedSelection.length &&
      updatedSelection.length !== selectedInboxes.length
    ) {
      onUpdateFilter(updatedSelection);
    }
  }, [inboxesMap, selectedInboxes, selectedTeamNamespaces, onUpdateFilter, selectedFilterValue]);

  /*
   * Event Handlers.
   */

  // Set the inboxes for the "is any of" filter. Does not actually apply the filters, since
  // the modal remains open after setting the inboxes.
  const onSetInboxes = (itemIds: ReadonlyArray<number>) => {
    dispatchAction({type: InboxesFilterActionTypesEnum.SET_INBOXES, values: itemIds});
  };

  // Apply the current selection.
  const onApplyClicked = () => {
    onUpdateFilter(state.selectedInboxes ?? state.specialValue);
    if (state.selectedInboxes) {
      trackOnEdit(state.selectedInboxes, previousSelection.selectedInboxes);
    } else {
      trackOnEdit([], previousSelection.selectedInboxes, {special_inbox_value: state.specialValue});
    }
  };

  // Cancel the current new selection, return to whatever was applied
  // before the filter was open.
  const onCancelClicked = () => {
    dispatchAction({type: InboxesFilterActionTypesEnum.UPDATE_SELECTED_VALUE, value: selectedFilterValue});
  };

  // Clear the filters, return to our default (the team selection).
  const onClearClicked = () => {
    dispatchAction({type: InboxesFilterActionTypesEnum.CLEAR});
    // Reset to our default.
    onUpdateFilter(inboxesFilterDefaultState.specialValue);
    trackOnRemove();
  };

  /*
   * Inboxes selection dropdown.
   */

  const onInboxesFetched = (inboxes: ReadonlyArray<AnalyticsInboxModel>) => {
    if (inboxes.length === 0) {
      return;
    }

    setFetchedInboxes((inboxesState) => inboxesState.concat(inboxes));
  };

  const analyticsInboxesFetcher = makeInboxesFetcher(
    company,
    namespace,
    selectedTeamNamespaces,
    onInboxesFetched,
  );

  const dropdownSource = computeFilterDropdownSourceAsync(analyticsInboxesFetcher);

  const computeFilterGenerator: FilterRowGenerator<AnalyticsInboxModel> = (
    item,
    dropdownSelectedIds,
    makeOnItemSelect,
  ) => {
    const isChecked = dropdownSelectedIds.includes(item.id);
    const onItemSelect = makeOnItemSelect(item.id);

    return {
      renderer: () => (
        <>
          <DropdownItemInboxDot inbox={item} />
          <UserThemeConsumer>
            {(theme) => (
              <DropdownItemContent isChecked={isChecked}>
                <StyledInboxItemWrapper>
                  {item.name}
                  {isTeammateNamespace(item.namespace) && (
                    <StyledIcon name="lockFilledTiny" color={theme.greys.shade70} />
                  )}
                </StyledInboxItemWrapper>
              </DropdownItemContent>
            )}
          </UserThemeConsumer>
        </>
      ),
      onSelect: onItemSelect,
      isSelected: isChecked,
    };
  };

  const shouldRenderIcon = isUndefined(selectedInboxes) || selectedInboxes.length === 0;

  const renderInboxesIsAnyOfButton = () => (
    <AnalyticsFilterButtonValue
      iconName={shouldRenderIcon ? iconName : undefined}
      selectedIds={selectedInboxes}
      itemsMap={inboxesMap}
      label={<Trans id="analytics-inboxes-selection-dropdown-inner-label">Select inboxes</Trans>}
      renderItem={renderButtonItem}
      shouldLabelSelection={false}
    />
  );

  const inboxesIsAnyOfFilter = () => (
    <ResourceMultiSelectFilter
      dropdownTitle={name}
      selectedItemIds={selectedInboxes}
      onSetItems={onSetInboxes}
      dropdownSource={dropdownSource}
      computeFilterGenerator={computeFilterGenerator}
      onClearAll={() => dispatchAction({type: InboxesFilterActionTypesEnum.CLEAR})}
      renderButtonValue={renderInboxesIsAnyOfButton}
    />
  );

  /*
   * Modal Dropdown.
   */

  const isActive = Boolean(previousSelection.specialValue || previousSelection.selectedInboxes.length > 0);

  const renderButton = () => (
    <AnalyticsFilterButtonValue
      iconName={isActive ? activeIconName : iconName}
      selectedIds={previousSelection.selectedInboxes}
      itemsMap={inboxesMap}
      label={computeButtonLabel(previousSelection)}
      renderItem={renderButtonItem}
      isActive={isActive}
    />
  );

  const computeSpecialValueRows = () =>
    specialValues.map((value) => ({
      id: value.id,
      value: value.name,
      isChecked: specialValue === value.value,
      onSelect: () => {
        dispatchAction({
          type: InboxesFilterActionTypesEnum.SELECT_SPECIAL_VALUE,
          value: value.value,
        });
      },
    }));

  const filterRows: ModalFilterItemProps[] = [
    {
      id: 'inboxes-filter-any-of',
      value: <Trans id="Analytics-inboxes-filter-modal-is-any-of">Is any of</Trans>,
      isChecked: isUndefined(specialValue),
      onSelect: () => {
        dispatchAction({
          type: InboxesFilterActionTypesEnum.SET_INBOXES,
          values: [],
        });
      },
      renderDropdown: inboxesIsAnyOfFilter,
    },
    ...computeSpecialValueRows(),
  ];

  const renderModalDropdown = (dropdownProps: BaseFilterDropdownProps) => (
    <ModalFilterDropdown
      {...dropdownProps}
      title={name}
      filters={filterRows}
      footerHandlers={{onApplyClicked, onCancelClicked}}
      onClear={onClearClicked}
      isActive={isActive}
      shouldCloseOnClear
    />
  );

  return {
    id: 'inboxes-filter',
    type: AnalyticsFilterTypesEnum.INBOXES,
    isActive,
    title: renderFilterTitle(name, iconName),
    renderDropdown: renderModalDropdown,
    renderButton,
  };
};

/*
 * Reducer.
 */

function inboxesFilterReducer(state: State, action: Action): State {
  switch (action.type) {
    case InboxesFilterActionTypesEnum.DESELECT_INBOX:
      if (isUndefined(state.selectedInboxes)) {
        return state;
      } // no-op

      return {
        ...state,
        specialValue: undefined,
        selectedInboxes: without(state.selectedInboxes, action.value.id),
      };

    case InboxesFilterActionTypesEnum.SET_INBOXES:
      return {
        ...state,
        specialValue: undefined,
        selectedInboxes: action.values,
      };

    case InboxesFilterActionTypesEnum.SELECT_SPECIAL_VALUE:
      return {
        ...state,
        specialValue: action.value,
        selectedInboxes: undefined,
      };

    case InboxesFilterActionTypesEnum.CLEAR:
      return inboxesFilterDefaultState;

    case InboxesFilterActionTypesEnum.UPDATE_SELECTED_VALUE:
      return parseSelectedValue(action.value);

    default:
      return state;
  }
}

/*
 * Helpers.
 */

function parseSelectedValue(
  selectedValue: AnalyticsPanelFiltersState[AnalyticsFilterTypesEnum.INBOXES],
): State {
  if (RAnalyticsInboxScopeFiltersEnum.is(selectedValue)) {
    return {
      specialValue: selectedValue,
      selectedInboxes: undefined,
    };
  }

  // Default value.
  if (!selectedValue || selectedValue.length === 0) {
    return inboxesFilterDefaultState;
  }

  return {
    specialValue: undefined,
    selectedInboxes: selectedValue,
  };
}

const renderButtonItem = (inbox: AnalyticsInboxModel) => (
  <UserThemeConsumer>
    {(theme) => (
      <Fragment key={inbox.id}>
        <StyledColorDot color={inboxHasColor(inbox) ? inbox.color : theme.greys.shade30} size={8} />
        {inbox.name}
      </Fragment>
    )}
  </UserThemeConsumer>
);

const computeButtonLabel = (previousSelection: State) => {
  if (!isUndefined(previousSelection.specialValue)) {
    return specialValuesMap[previousSelection.specialValue].name;
  }

  return name;
};
